import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Security as SecurityIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '@/core/contexts/AuthContext';
import { useNotification } from '@/core/contexts/NotificationContext';
import useAuthConfig from '@/hooks/useAuthConfig';
import { FONT_SIZE, TOUCH } from '@/core/platform/platformStandards';

/** MFA code entry — mobile-first form inside AuthLayout. @viewport both */
const MFAVerification = () => {
  const { verifyMFA, mfaMessage, clearMFA } = useAuth();
  const { notifySuccess, notifyInfo } = useNotification();
  const { mfaCodeLength = 6, mfaCodeExpiryMinutes = 5 } = useAuthConfig();
  
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(mfaCodeExpiryMinutes * 60);
  const [canResend, setCanResend] = useState(false);
  
  const inputRef = useRef(null);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setCanResend(true), 30000);
    return () => clearTimeout(timer);
  }, []);

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= mfaCodeLength) {
      setMfaCode(value);
      setError('');
      
      if (value.length === mfaCodeLength) {
        handleVerify(value);
      }
    }
  };

  const handleVerify = async (code = mfaCode) => {
    if (code.length !== mfaCodeLength) {
      setError(`Please enter a ${mfaCodeLength}-digit code`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verifyMFA(code);
      if (result.mfa_verified) {
        notifySuccess('MFA verification successful! Please login again to complete the process.');
        clearMFA();
        window.location.reload();
      }
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
      setMfaCode('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    notifyInfo('Please login again to receive a new verification code.');
    clearMFA();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const displayMessage = mfaMessage || `We've sent a verification code to your email.`;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <SecurityIcon 
          sx={{ 
            // intentional: decorative illustration-size MFA security icon
            fontSize: 40, 
            color: 'primary.main', 
            mb: 2 
          }} 
        />
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Verify Your Identity
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {displayMessage}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          ref={inputRef}
          label="Verification Code"
          value={mfaCode}
          onChange={handleCodeChange}
          placeholder={`Enter ${mfaCodeLength}-digit code`}
          fullWidth
          inputProps={{
            maxLength: mfaCodeLength,
            inputMode: 'numeric',
            enterKeyHint: 'go',
            autoComplete: 'one-time-code'
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: TOUCH.minSize,
              '& fieldset': {
                borderWidth: 2
              }
            },
            '& input': {
              textAlign: 'center',
              fontSize: FONT_SIZE.xxl,
              letterSpacing: '0.5rem',
              fontFamily: 'monospace'
            }
          }}
        />
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            mt: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Code expires in: {formatTime(timeLeft)}
          </Typography>
          <Button
            onClick={handleResendCode}
            disabled={!canResend || loading}
            startIcon={loading ? <CircularProgress size={TOUCH.iconSize} /> : <RefreshIcon />}
            sx={{ minHeight: TOUCH.minSize, alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            {loading ? 'Sending...' : 'Resend Code'}
          </Button>
        </Box>
      </Box>

      <Button
        onClick={() => handleVerify()}
        fullWidth
        variant="contained"
        size="large"
        disabled={loading || mfaCode.length !== mfaCodeLength}
        sx={{
          py: 1.5,
          mb: 3,
          minHeight: TOUCH.minSize,
        }}
      >
        {loading ? (
          <CircularProgress size={TOUCH.iconSize} color="inherit" />
        ) : (
          'Verify Code'
        )}
      </Button>

      <Button
        onClick={clearMFA}
        fullWidth
        variant="outlined"
        startIcon={<BackIcon />}
        sx={{ mb: 2, minHeight: TOUCH.minSize }}
      >
        Back to Login
      </Button>

      <Paper 
        sx={(theme) => ({ 
          p: 2, 
          background: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        })}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>Didn&apos;t receive the code?</strong>
          <br />
          • Check your spam/junk folder
          <br />
          • Wait 30 seconds before requesting a new code
          <br />
          • Contact support if issues persist
        </Typography>
      </Paper>
    </Box>
  );
};

export default MFAVerification;
